import React from 'react';
import { motion } from 'framer-motion';
import { ModuleCard } from '../components/ModuleCard';
import { CategoryCard } from '../components/CategoryCard';
import { CourseItem } from '../components/CourseItem';
import { ArrowRight, Music, Mic, PlayCircle } from 'lucide-react';
interface HomePageProps {
  onNavigate: (page: any, params?: any) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  return <div className="min-h-screen w-full flex flex-col bg-white font-chinese" data-id="element-153">
      {/* Navigation */}
      <nav className="w-full px-6 py-4 flex items-center justify-between max-w-7xl mx-auto sticky top-0 bg-white/80 backdrop-blur-md z-50 border-b border-slate-100" data-id="element-154">
        <div className="flex items-center space-x-2" data-id="element-155">
          <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white text-xl shadow-lg shadow-blue-200" data-id="element-156">
            🎵
          </div>
          <span className="text-xl font-bold text-slate-800 font-display" data-id="element-157">
            同唱古诗词
          </span>
        </div>

        <div className="hidden md:flex items-center space-x-8" data-id="element-158">
          <a href="#basics" className="text-slate-600 font-medium hover:text-blue-600 transition-colors" data-id="element-159">
            音乐基础
          </a>
          <a href="#instruments" className="text-slate-600 font-medium hover:text-blue-600 transition-colors" data-id="element-160">
            乐器探索
          </a>
          <a href="#courses" className="text-slate-600 font-medium hover:text-blue-600 transition-colors" data-id="element-161">
            古诗课程
          </a>
        </div>

        <div className="flex items-center space-x-4" data-id="element-162">
          <button className="text-blue-600 font-bold hover:text-blue-700" data-id="element-163">
            登录
          </button>
          <button className="bg-blue-600 text-white px-5 py-2 rounded-full font-bold hover:bg-blue-700 shadow-md shadow-blue-200 transition-all hover:scale-105" data-id="element-164">
            注册
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="pt-16 pb-24 text-center relative overflow-hidden bg-gradient-to-b from-blue-50/50 to-white" data-id="element-165">
        <motion.div initial={{
        y: -20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        duration: 0.6
      }} className="relative z-10 max-w-4xl mx-auto px-4" data-id="element-166">
          <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-bold mb-6" data-id="element-167">
            ✨ 专为儿童设计的音乐启蒙之旅
          </div>
          <h1 className="text-5xl md:text-7xl font-extrabold text-blue-900 mb-6 tracking-tight font-display" data-id="element-168">
            让音乐与古诗
            <br data-id="element-169" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600" data-id="element-170">
              在此相遇
            </span>
          </h1>
          <p className="text-xl text-slate-500 font-medium mb-12 max-w-2xl mx-auto" data-id="element-171">
            遵循 "乐理 → 乐器 → 应用" 的科学学习路径，
            <br data-id="element-172" />
            在游戏中培养乐感，在吟唱中传承文化。
          </p>

          <button onClick={() => document.getElementById('basics')?.scrollIntoView({
          behavior: 'smooth'
        })} className="bg-blue-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl shadow-blue-200 hover:bg-blue-700 hover:scale-105 transition-all flex items-center mx-auto" data-id="element-173">
            开始学习之旅 <ArrowRight className="ml-2" data-id="element-174" />
          </button>
        </motion.div>

        {/* Decorative Background Elements */}
        <div className="absolute top-1/2 left-10 w-32 h-32 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" data-id="element-175"></div>
        <div className="absolute top-1/2 right-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" data-id="element-176"></div>
      </header>

      {/* Section 1: Music Basics */}
      <section id="basics" className="py-20 bg-white relative" data-id="element-177">
        <div className="max-w-7xl mx-auto px-6" data-id="element-178">
          <div className="flex items-end justify-between mb-12" data-id="element-179">
            <div data-id="element-180">
              <div className="flex items-center space-x-3 mb-2" data-id="element-181">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold" data-id="element-182">
                  1
                </span>
                <h2 className="text-3xl font-bold text-slate-900 font-display" data-id="element-183">
                  音乐基础
                </h2>
              </div>
              <p className="text-slate-500 ml-11" data-id="element-184">
                通过趣味游戏，掌握音乐的四大基本元素
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-185">
            <ModuleCard title="音高训练" subtitle="听音辨高低" icon="🎵" bgColor="#DBEAFE" textColor="#1E40AF" onClick={() => onNavigate('immersive')} delay={0.1} data-id="element-186" />
            <ModuleCard title="节奏大师" subtitle="跟随节拍律动" icon="🥁" bgColor="#D1FAE5" textColor="#065F46" onClick={() => onNavigate('rhythm')} delay={0.2} data-id="element-187" />
            <ModuleCard title="旋律感知" subtitle="线条与起伏" icon="🎼" bgColor="#F3E8FF" textColor="#6B21A8" onClick={() => onNavigate('melody')} delay={0.3} data-id="element-188" />
            <ModuleCard title="情绪调色盘" subtitle="大调与小调" icon="🎨" bgColor="#FFEDD5" textColor="#9A3412" onClick={() => onNavigate('emotion')} delay={0.4} data-id="element-189" />
          </div>
        </div>
      </section>

      {/* Section 2: Instrument Exploration */}
      <section id="instruments" className="py-20 bg-slate-50 border-y border-slate-100" data-id="element-190">
        <div className="max-w-7xl mx-auto px-6" data-id="element-191">
          <div className="flex items-end justify-between mb-12" data-id="element-192">
            <div data-id="element-193">
              <div className="flex items-center space-x-3 mb-2" data-id="element-194">
                <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold" data-id="element-195">
                  2
                </span>
                <h2 className="text-3xl font-bold text-slate-900 font-display" data-id="element-196">
                  乐器探索
                </h2>
              </div>
              <p className="text-slate-500 ml-11" data-id="element-197">
                认识中国传统乐器，聆听千年的声音
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-198">
            <ModuleCard title="古筝" icon="🎹" bgColor="#FEF3C7" textColor="#92400E" onClick={() => onNavigate('instrument', {
            id: 'guzheng'
          })} data-id="element-199" />
            <ModuleCard title="琵琶" icon="🪕" bgColor="#E0E7FF" textColor="#3730A3" onClick={() => onNavigate('instrument', {
            id: 'pipa'
          })} data-id="element-200" />
            <ModuleCard title="二胡" icon="🎻" bgColor="#FFEDD5" textColor="#9A3412" onClick={() => onNavigate('instrument', {
            id: 'erhu'
          })} data-id="element-201" />
            <ModuleCard title="笛子" icon="🪈" bgColor="#D1FAE5" textColor="#065F46" onClick={() => onNavigate('instrument', {
            id: 'dizi'
          })} data-id="element-202" />
            <ModuleCard title="古琴" icon="🎸" bgColor="#F3E8FF" textColor="#6B21A8" onClick={() => onNavigate('instrument', {
            id: 'guqin'
          })} data-id="element-203" />
            <ModuleCard title="编钟" icon="🔔" bgColor="#FEE2E2" textColor="#991B1B" onClick={() => onNavigate('instrument', {
            id: 'bianzhong'
          })} data-id="element-204" />
          </div>
        </div>
      </section>

      {/* Section 3: Poem Courses */}
      <section id="courses" className="py-20 bg-white" data-id="element-205">
        <div className="max-w-7xl mx-auto px-6" data-id="element-206">
          <div className="flex items-end justify-between mb-12" data-id="element-207">
            <div data-id="element-208">
              <div className="flex items-center space-x-3 mb-2" data-id="element-209">
                <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold" data-id="element-210">
                  3
                </span>
                <h2 className="text-3xl font-bold text-slate-900 font-display" data-id="element-211">
                  古诗词课程
                </h2>
              </div>
              <p className="text-slate-500 ml-11" data-id="element-212">
                综合应用所学，完成 "听·看·唱·演" 四步挑战
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-213">
            <div onClick={() => onNavigate('course', {
            id: 'yonge'
          })} className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl transition-all cursor-pointer flex items-center space-x-6" data-id="element-214">
              <div className="w-24 h-24 bg-blue-50 rounded-xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform" data-id="element-215">
                🦢
              </div>
              <div className="flex-1" data-id="element-216">
                <div className="flex items-center justify-between mb-2" data-id="element-217">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-218">咏鹅</h3>
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-bold" data-id="element-219">
                    入门
                  </span>
                </div>
                <p className="text-slate-500 text-sm mb-4" data-id="element-220">
                  唐·骆宾王 | 适合3-5岁
                </p>
                <div className="flex items-center space-x-2 text-sm font-medium text-slate-400" data-id="element-221">
                  <span className="flex items-center" data-id="element-222">
                    <Music size={14} className="mr-1" data-id="element-223" /> 节奏
                  </span>
                  <span className="flex items-center" data-id="element-224">
                    <Mic size={14} className="mr-1" data-id="element-225" /> 跟唱
                  </span>
                  <span className="flex items-center" data-id="element-226">
                    <PlayCircle size={14} className="mr-1" data-id="element-227" /> 4课时
                  </span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-228">
                <ArrowRight size={20} data-id="element-229" />
              </div>
            </div>

            <div onClick={() => onNavigate('course', {
            id: 'jingyesi'
          })} className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl transition-all cursor-pointer flex items-center space-x-6" data-id="element-230">
              <div className="w-24 h-24 bg-purple-50 rounded-xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform" data-id="element-231">
                🌙
              </div>
              <div className="flex-1" data-id="element-232">
                <div className="flex items-center justify-between mb-2" data-id="element-233">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-234">静夜思</h3>
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-bold" data-id="element-235">
                    进阶
                  </span>
                </div>
                <p className="text-slate-500 text-sm mb-4" data-id="element-236">
                  唐·李白 | 适合5-7岁
                </p>
                <div className="flex items-center space-x-2 text-sm font-medium text-slate-400" data-id="element-237">
                  <span className="flex items-center" data-id="element-238">
                    <Music size={14} className="mr-1" data-id="element-239" /> 旋律
                  </span>
                  <span className="flex items-center" data-id="element-240">
                    <Mic size={14} className="mr-1" data-id="element-241" /> 情感
                  </span>
                  <span className="flex items-center" data-id="element-242">
                    <PlayCircle size={14} className="mr-1" data-id="element-243" /> 4课时
                  </span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-244">
                <ArrowRight size={20} data-id="element-245" />
              </div>
            </div>

            <div onClick={() => onNavigate('course', {
            id: 'chunxiao'
          })} className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl transition-all cursor-pointer flex items-center space-x-6" data-id="element-246">
              <div className="w-24 h-24 bg-green-50 rounded-xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform" data-id="element-247">
                🌸
              </div>
              <div className="flex-1" data-id="element-248">
                <div className="flex items-center justify-between mb-2" data-id="element-249">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-250">春晓</h3>
                  <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-bold" data-id="element-251">
                    入门
                  </span>
                </div>
                <p className="text-slate-500 text-sm mb-4" data-id="element-252">
                  唐·孟浩然 | 适合3-5岁
                </p>
                <div className="flex items-center space-x-2 text-sm font-medium text-slate-400" data-id="element-253">
                  <span className="flex items-center" data-id="element-254">
                    <Music size={14} className="mr-1" data-id="element-255" /> 强弱
                  </span>
                  <span className="flex items-center" data-id="element-256">
                    <Mic size={14} className="mr-1" data-id="element-257" /> 朗诵
                  </span>
                  <span className="flex items-center" data-id="element-258">
                    <PlayCircle size={14} className="mr-1" data-id="element-259" /> 4课时
                  </span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-260">
                <ArrowRight size={20} data-id="element-261" />
              </div>
            </div>

            <div onClick={() => onNavigate('course', {
            id: 'minnong'
          })} className="group bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-xl transition-all cursor-pointer flex items-center space-x-6" data-id="element-262">
              <div className="w-24 h-24 bg-yellow-50 rounded-xl flex items-center justify-center text-4xl group-hover:scale-110 transition-transform" data-id="element-263">
                🌾
              </div>
              <div className="flex-1" data-id="element-264">
                <div className="flex items-center justify-between mb-2" data-id="element-265">
                  <h3 className="text-xl font-bold text-slate-900" data-id="element-266">悯农</h3>
                  <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-bold" data-id="element-267">
                    进阶
                  </span>
                </div>
                <p className="text-slate-500 text-sm mb-4" data-id="element-268">
                  唐·李绅 | 适合6-8岁
                </p>
                <div className="flex items-center space-x-2 text-sm font-medium text-slate-400" data-id="element-269">
                  <span className="flex items-center" data-id="element-270">
                    <Music size={14} className="mr-1" data-id="element-271" /> 节奏
                  </span>
                  <span className="flex items-center" data-id="element-272">
                    <Mic size={14} className="mr-1" data-id="element-273" /> 表演
                  </span>
                  <span className="flex items-center" data-id="element-274">
                    <PlayCircle size={14} className="mr-1" data-id="element-275" /> 4课时
                  </span>
                </div>
              </div>
              <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-276">
                <ArrowRight size={20} data-id="element-277" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-50 border-t border-slate-100 text-center" data-id="element-278">
        <div className="flex items-center justify-center space-x-2 mb-4" data-id="element-279">
          <span className="font-bold text-slate-700" data-id="element-280">Sharky</span>
          <span className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-281"></span>
          <span className="text-slate-500" data-id="element-282">监制</span>
        </div>
        <p className="text-slate-400 text-sm" data-id="element-283">© 2026 CC 技术支持</p>
      </footer>
    </div>;
}